import React from "react";
import { Step, Section, ManualShell } from "@/pages/manual/ManualKit";
import {
  ShieldCheck, LogIn, ClipboardCheck, LayoutDashboard, Table2, Settings,
  GraduationCap, Mail, Clock, Lock, HelpCircle, Users, RefreshCw, Bell, GitBranch,
} from "lucide-react";

const Node = ({ children, tone = "slate", level = 0 }) => {
  const tones = {
    slate: "border-slate-300 text-slate-700",
    green: "border-emerald-300 text-emerald-800 bg-emerald-50",
    amber: "border-amber-300 text-amber-800 bg-amber-50",
    red: "border-red-300 text-red-800 bg-red-50",
    blue: "border-blue-300 text-blue-800 bg-blue-50",
  };
  return (
    <div style={{ marginLeft: level * 20 }} className="mb-1.5">
      <span className={`inline-block text-sm rounded-lg border px-3 py-1.5 ${tones[tone]}`}>{children}</span>
    </div>
  );
};

export default function ManualEvaluador() {
  return (
    <ManualShell
      testid="manual-evaluador"
      pdf="evaluador.pdf"
      badge="Uso interno del comité"
      title="Manual del Evaluador / Administrador"
      intro="Guía para el comité de becas: acceso seguro, revisión y resolución de solicitudes, administración de la tabla de beneficios y el Portal Instituto Hebreo."
    >
      <Section id="que-es" icon={ShieldCheck} title="Visión general" subtitle="Qué hace la plataforma">
        <p className="text-sm text-foreground leading-relaxed">
          Los socios postulan a una rebaja de su cuota completando un asistente de 5 pasos y adjuntando respaldos.
          El comité revisa cada caso en el panel de administración, apoyado por una <strong>tabla de beneficios</strong>
          (categoría × tamaño familiar × ingreso ajustado) que entrega una recomendación de porcentaje. La
          <strong> decisión final la toma el comité</strong> y todo queda registrado en la auditoría.
        </p>
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-slate-700">
          <strong>Roles:</strong> <em>Evaluador</em> revisa y resuelve; <em>Administrador</em> además edita la tabla y la configuración;
          <em> Superadministrador</em> gestiona las cuentas de otros evaluadores/administradores.
        </div>
      </Section>

      <Section id="arbol" icon={GitBranch} title="Árbol de decisión del proceso" subtitle="Recorrido completo de una solicitud">
        <div className="bg-secondary/40 rounded-xl p-5 overflow-x-auto">
          <p className="font-semibold text-primary mb-3">1) Envío de la solicitud</p>
          <Node level={0}>¿El titular está en la base EIM?</Node>
          <Node level={1} tone="green">Sí → acceso automático · estado <strong>En revisión</strong></Node>
          <Node level={1} tone="amber">No → <strong>En validación de ingreso</strong> · el comité da OK o rechaza</Node>
          <Node level={2} tone="green">OK → <strong>En revisión</strong> (con acceso al club)</Node>
          <Node level={2} tone="red">Rechazo → <strong>Cuarentena</strong></Node>
          <Node level={3} tone="blue">El postulante ingresa 3 socios recomendantes</Node>
          <Node level={3} tone="slate">Los socios confirman por correo (con moderación); al responder los 3 se avisa a atención a socios</Node>
          <Node level={3} tone="amber">Vuelve a <strong>validación de ingreso (2ª ronda)</strong> · el comité da OK o rechaza</Node>
          <Node level={4} tone="green">OK → <strong>En revisión</strong></Node>
          <Node level={4} tone="red">Rechazo → <strong>cuarentena definitiva</strong> (solo comunidad judía)</Node>

          <p className="font-semibold text-primary mt-4 mb-3">2) En revisión (evaluación con tabla de beneficios e IH)</p>
          <Node level={0}>Solicitar información adicional → el socio responde → vuelve a <strong>En revisión</strong></Node>
          <Node level={0}>Consultar beca del Instituto Hebreo → responde la asistente social</Node>
          <Node level={0} tone="blue">Resolver → <strong>Procesada</strong> (aprobar, proponer % distinto o rechazar)</Node>

          <p className="font-semibold text-primary mt-4 mb-3">3) Procesada</p>
          <Node level={0} tone="green">Si el % coincide con lo solicitado → el socio acepta</Node>
          <Node level={0} tone="amber">Si el % es distinto → el socio puede Aceptar, Rechazar o Solicitar reevaluación</Node>
          <Node level={1}>La reevaluación (con información y documentos nuevos) devuelve la solicitud a <strong>En revisión</strong></Node>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 mt-3">
          <strong>Regla transversal:</strong> solo se autoriza el ingreso a personas de la <strong>comunidad judía</strong>, aunque un no socio reúna las 3 recomendaciones.
        </div>
      </Section>

      <Section id="ingreso" icon={LogIn} title="1. Ingreso seguro (doble factor)" subtitle="Cómo acceder al panel">
        <Step n="1" title="Login con RUT + contraseña">
          Ingresa por <strong>/ingresar</strong> con tu RUT y contraseña personal.
        </Step>
        <Step n="2" title="Código de acceso (MFA)">
          Tras la contraseña se envía un <strong>código de un solo uso a tu correo</strong>, que debes ingresar para
          completar el acceso. Esto protege el panel del comité.
        </Step>
      </Section>

      <Section id="panel" icon={LayoutDashboard} title="2. Panel del comité" subtitle="Revisión y resolución de solicitudes">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="bg-secondary/50 rounded-xl p-4 text-sm">
            <p className="font-semibold text-primary flex items-center gap-2"><LayoutDashboard className="w-4 h-4" /> Dashboard de solicitudes</p>
            <p className="text-xs text-muted-foreground mt-1">Lista todas las solicitudes con su estado, filtros y buscador. Marca los casos fuera de la base EIM.</p>
          </div>
          <div className="bg-secondary/50 rounded-xl p-4 text-sm">
            <p className="font-semibold text-primary flex items-center gap-2"><ClipboardCheck className="w-4 h-4" /> Ficha de la solicitud</p>
            <p className="text-xs text-muted-foreground mt-1">Detalle de familia, ingresos, gastos y documentos. Aquí se resuelve el % final, se pide información adicional o se consulta al Instituto Hebreo.</p>
          </div>
          <div className="bg-secondary/50 rounded-xl p-4 text-sm">
            <p className="font-semibold text-primary flex items-center gap-2"><Table2 className="w-4 h-4" /> Tabla de beneficios</p>
            <p className="text-xs text-muted-foreground mt-1">Define los tramos (categoría × tamaño familiar × ingreso) y el porcentaje sugerido. Editable por administradores.</p>
          </div>
          <div className="bg-secondary/50 rounded-xl p-4 text-sm">
            <p className="font-semibold text-primary flex items-center gap-2"><Settings className="w-4 h-4" /> Configuración</p>
            <p className="text-xs text-muted-foreground mt-1">Campaña, textos, correo de la asistente social IH, correo de resumen e importación de la base real (con panel de estado de la base cargada).</p>
          </div>
        </div>
        <div className="bg-secondary/50 rounded-xl p-4 text-sm">
          <p className="font-semibold text-primary flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Auditoría</p>
          <p className="text-xs text-muted-foreground mt-1">Registro trazable de acciones (quién hizo qué y cuándo), como respaldo del proceso.</p>
        </div>
      </Section>

      <Section id="resolver" icon={ClipboardCheck} title="3. Cómo resolver una solicitud" subtitle="Flujo recomendado">
        <Step n="1" title="Abrir la ficha">
          Desde el dashboard, abre la solicitud a revisar. Revisa familia, ingresos, gastos y documentos adjuntos.
        </Step>
        <Step n="2" title="Contrastar con la recomendación">
          El sistema muestra el porcentaje sugerido según la tabla de beneficios. Úsalo como referencia, no como decisión automática.
        </Step>
        <Step n="3" title="Pedir información adicional (si aplica)">
          Si faltan antecedentes, solicita <strong>información adicional</strong>: al postulante le llega un aviso por correo.
        </Step>
        <Step n="4" title="Consultar beca del Instituto Hebreo (si aplica)">
          Si la familia declara beca IH, envía la consulta a la asistente social; ella responde desde su portal protegido.
        </Step>
        <Step n="5" title="Resolver el porcentaje final">
          Define el % final y cierra el caso. Se envía un correo de resumen interno y queda registrado en auditoría.
        </Step>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-slate-700 mt-2">
          <strong>Base de cálculo:</strong> la evaluación y la tabla de beneficios trabajan sobre el <strong>ingreso bruto</strong>
          (antes de descuentos), no el líquido, porque las tablas fueron confeccionadas desde el monto bruto. Los
          <strong> gastos extraordinarios</strong> (médicos, necesidades especiales/discapacidad, vivienda adicional por
          separación, apoyo a familiares y otros recurrentes e inevitables) se descuentan del ingreso bruto para obtener el
          <strong> ingreso ajustado</strong>; cada ítem puede aceptarse, rechazarse o ajustarse desde la ficha.
        </div>
      </Section>

      <Section id="ingreso-validacion" icon={Users} title="4. Validación de ingreso (socios y no socios)" subtitle="Base vs. fuera de base">
        <p className="text-sm text-foreground leading-relaxed">
          Al enviar una solicitud, el sistema distingue si el titular está en la base de socios EIM:
        </p>
        <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1.5">
          <li><strong>Socio en base:</strong> obtiene acceso automático a las instalaciones y la solicitud pasa directo a “En revisión”.</li>
          <li><strong>No socio:</strong> la solicitud queda “En validación de ingreso” y el comité debe <strong>dar OK o rechazar</strong> el ingreso desde la ficha antes de evaluar.</li>
        </ul>
        <div className="bg-secondary/50 rounded-xl p-4 text-sm flex items-start gap-3">
          <Bell className="w-4 h-4 text-accent mt-0.5" />
          <span className="text-xs text-muted-foreground"><strong className="text-primary">Recordatorios:</strong> si una validación de ingreso queda sin resolver, se envía un aviso a <strong>becas@eimbecas.cl</strong> a las 72h y luego cada 24h hasta resolver.</span>
        </div>
      </Section>

      <Section id="recomendadores" icon={Users} title="5. Recomendadores y segunda decisión" subtitle="Postulantes no socios rechazados">
        <p className="text-sm text-foreground leading-relaxed">
          Si el comité <strong>rechaza el ingreso</strong> de un no socio, la solicitud pasa a cuarentena y se invita al
          postulante a indicar <strong>3 socios del EIM que lo recomienden</strong> (nombre, RUT y correo).
        </p>
        <p className="text-sm text-foreground leading-relaxed">
          Cuando el postulante los ingresa, llega un correo a becas@ y la solicitud vuelve a “En validación de ingreso”.
          Cada uno de los 3 socios recibe además un <strong>correo para confirmar</strong> que recomienda al postulante e
          indicar de dónde lo conoce; su respuesta aparece en la ficha como antecedente. El comité toma una
          <strong> segunda decisión</strong> de ingreso. Un segundo rechazo es definitivo.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
          <strong>Importante (atención a socios):</strong> solo se autoriza el ingreso a <strong>personas de la comunidad judía</strong>.
          Una persona no comunitaria <strong>no será autorizada aunque cuente con las 3 recomendaciones</strong>. La decisión la toma
          atención a socios según las recomendaciones; en casos extremos puede pedir autorización al comité.
        </div>
      </Section>

      <Section id="reevaluacion" icon={RefreshCw} title="6. Reevaluación con documentación" subtitle="Solicitudes ya procesadas">
        <p className="text-sm text-foreground leading-relaxed">
          Cuando una solicitud está procesada y el postulante pide una <strong>reevaluación</strong>, debe aportar
          información nueva y <strong>al menos un documento de respaldo</strong>. El caso vuelve a “En revisión” con la
          nueva documentación para que el comité lo analice otra vez; también llega un aviso a becas@.
        </p>
      </Section>

      <Section id="ih" icon={GraduationCap} title="7. Portal Instituto Hebreo" subtitle="Para la asistente social del IH">
        <p className="text-sm text-foreground leading-relaxed">
          Cuando una familia declara <strong>beca del Instituto Hebreo</strong>, el caso llega a la
          <strong> asistente social del IH</strong>, que ingresa a su área protegida en <strong>/portal-ih</strong>
          (correo y contraseña, sin ver datos financieros de la familia) y <strong>confirma o corrige el porcentaje</strong>,
          o lo deja <em>“En revisión”</em> si necesita esperar.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="bg-secondary/50 rounded-xl p-4 text-sm flex items-start gap-3">
            <Mail className="w-4 h-4 text-accent mt-0.5" />
            <span className="text-xs text-muted-foreground"><strong className="text-primary">Aviso por correo:</strong> cada nuevo caso se avisa con enlace directo a su portal. El primer acceso incluye un enlace para crear su contraseña.</span>
          </div>
          <div className="bg-secondary/50 rounded-xl p-4 text-sm flex items-start gap-3">
            <Clock className="w-4 h-4 text-accent mt-0.5" />
            <span className="text-xs text-muted-foreground"><strong className="text-primary">Plazo y recordatorios:</strong> se solicita responder en un máximo de <strong>72 horas</strong>. Si no resuelve, se envía un recordatorio a las 72h y luego cada 24h hasta resolver.</span>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
          <strong>Consentimiento obligatorio:</strong> la beca IH solo se considera si el socio <strong>autorizó expresamente</strong> la
          consulta con la asistente social del colegio. Ese consentimiento se muestra como un <strong>sello</strong> en la ficha del
          panel y en el portal IH. <strong>Si el socio no autorizó, la beca IH no se incluye</strong> en la evaluación y no se envía la consulta.
        </div>
      </Section>

      <Section id="privacidad" icon={Lock} title="8. Privacidad y seguridad">
        <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1.5">
          <li>La información se almacena <strong>cifrada</strong> y se usa solo para evaluar la solicitud.</li>
          <li>El acceso del comité es con <strong>RUT + contraseña + código de un solo uso</strong> (doble factor).</li>
          <li>La asistente social del IH <strong>nunca ve información financiera</strong> de la familia.</li>
          <li>Todas las acciones quedan registradas en la <strong>auditoría</strong>.</li>
        </ul>
      </Section>

      <Section id="soporte" icon={HelpCircle} title="9. Soporte">
        <p className="text-sm text-muted-foreground">Ante cualquier duda del comité, escribe a <strong>becas@eimbecas.cl</strong>.</p>
      </Section>
    </ManualShell>
  );
}
